import React, { useEffect } from 'react';
import { FolderIcon, FolderOpenIcon } from '@heroicons/react/24/outline';

import {
	deleteLocation,
	getLocations,
	editLocation,
} from '../../api/locations';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import { classNames, createDataTree } from '../../components/helpers/functions';

import useModal from '../../hooks/useModal';
import useAsync from '../../hooks/useAsync';

const Locations = () => {
	// Get `addLocation` modal information
	const { open, setOpen: openLocationModal } = useModal('addLocation');

	// Setup `deleteModal` generic modal
	const { setClose: closeDeleteModal, setOpen: openDeleteModal } =
		useModal('deleteModal');

	// Get locations hook
	const { value: locations, execute: refreshLocations } =
		useAsync(getLocations);

	// Update location
	const { execute: onUpdateLocation } = useAsync(editLocation, {
		immediate: false,
	});

	// Delete location hook
	const { execute: onDelete } = useAsync(deleteLocation, { immediate: false });

	// Refresh locations when closing `addLocation` modal
	useEffect(() => {
		if (!open) {
			refreshLocations();
		}
	}, [open]);

	// Create data tree from locations
	const dataTree = createDataTree(locations?.rows || []);

	// List Component
	const ChildItem = ({ child, depth = 1 }) => {
		return (
			<>
				<li
					key={child.id}
					className="px-4 py-4 sm:px-6 flex items-center justify-between"
				>
					<div
						className="flex items-center"
						style={{
							marginLeft: `${1.75 * (depth - 1)}rem`,
						}}
					>
						{child.children.length ? (
							<>
								<FolderOpenIcon
									className="h-5 w-5 mr-2 text-gray-500"
									aria-hidden="true"
									strokeWidth={1}
								/>
							</>
						) : (
							<FolderIcon
								className="h-5 w-5 mr-2 text-gray-500"
								aria-hidden="true"
								strokeWidth={1}
							/>
						)}
						{`${child.name} ${child?.isDefault ? ' - Default' : ''}`}
					</div>
					<div>
						{/*!child.isDefault && !child?.parent_id && (
							<a
								className={classNames(
									'ml-4 cursor-pointer text-indigo-600 hover:text-indigo-900'
								)}
								onClick={() => {
									onUpdateLocation({ id: child.id, isDefault: true }, () =>
										refreshLocations()
									);
								}}
							>
								Set as Default
							</a>
							)*/}

						<a
							className={classNames(
								'ml-4 cursor-pointer text-indigo-600 hover:text-indigo-900',
							)}
							onClick={() => openLocationModal(child)}
						>
							Edit
						</a>

						<a
							className={classNames(
								'ml-4 cursor-pointer text-red-500 hover:text-red-800',
							)}
							onClick={() =>
								openDeleteModal({
									title: `Deleting location: ${child.name}`,
									confirmText: 'Delete Location',
									onConfirm: () =>
										onDelete({ id: child.id }, () => {
											refreshLocations();
											closeDeleteModal();
										}),
								})
							}
						>
							Delete
						</a>
					</div>
				</li>
				{child?.children?.length
					? child.children.map((c, i) => (
							<ChildItem child={c} depth={depth + 1} />
						))
					: null}
			</>
		);
	};

	return (
		<MainContent maxWidth="max-w-7xl">
			<PageHeading
				actions={[{ name: 'Add Location', onClick: () => openLocationModal() }]}
				divider={false}
			>
				{`Locations (${locations?.rows?.length || 0})`}
			</PageHeading>
			<div className="grid grid-cols-1">
				<div className="overflow-hidden bg-white shadow sm:rounded-md">
					<ul role="list" className="divide-y divide-gray-200 text-xs">
						{dataTree
							.sort((a, b) => b.isDefault - a.isDefault)
							.map((node) => (
								<ChildItem child={node} />
							))}
					</ul>
				</div>
			</div>
		</MainContent>
	);
};

export default Locations;
